import React, { useEffect, useMemo, useState } from 'react';
import { Filter, Network, RefreshCcw } from 'lucide-react';
import {
  Button,
  Card,
  CardHeader,
  EmptyState,
  PageSection,
  SearchInput,
  Select,
  StatusBadge,
} from '@/ui/components';
import {
  companyAiService,
} from '@/services/companyAiService';
import type {
  CompanyAiGraphVisualEdge,
  CompanyAiGraphVisualNode,
  CompanyAiGraphVisualization,
} from '@/services/companyAiService';

type GraphNodeWithPosition = CompanyAiGraphVisualNode & {
  x: number;
  y: number;
  color: string;
  lane: 'focus' | 'incoming' | 'outgoing' | 'overview' | 'detail' | 'support' | 'other';
};

type GraphRagVisualizerProps = {
  canAccess: boolean;
};

type GraphViewMode = 'detail' | 'overview';
type GraphLabelMode = 'key' | 'all' | 'none';
type ProjectDetailStageId = 'files' | 'task' | 'invoice' | 'project' | 'email' | 'support';

const ENTITY_TYPE_OPTIONS = [
  { value: 'all', label: 'All types' },
  { value: 'project', label: 'Projects' },
  { value: 'quotation', label: 'Quotations' },
  { value: 'invoice', label: 'Invoices' },
  { value: 'task', label: 'Tasks' },
  { value: 'email', label: 'Emails' },
  { value: 'address', label: 'Addresses' },
  { value: 'user', label: 'Users' },
];

const LIMIT_OPTIONS = [
  { value: '36', label: '36 nodes' },
  { value: '72', label: '72 nodes' },
  { value: '120', label: '120 nodes' },
  { value: '180', label: '180 nodes' },
];

const VIEW_MODE_OPTIONS = [
  { value: 'detail', label: 'Project detail' },
  { value: 'overview', label: 'Overview' },
];

const MIN_CONFIDENCE_OPTIONS = [
  { value: '0', label: 'Any confidence' },
  { value: '0.5', label: '50%+' },
  { value: '0.75', label: '75%+' },
  { value: '0.9', label: '90%+' },
];

const LABEL_MODE_OPTIONS = [
  { value: 'key', label: 'Key labels' },
  { value: 'all', label: 'All labels' },
  { value: 'none', label: 'No labels' },
];

const ENTITY_COLORS: Record<string, string> = {
  project: '#2251b4',
  quotation: '#0f8a63',
  invoice: '#c06c16',
  task: '#7c3aed',
  email: '#be185d',
  address: '#0369a1',
  user: '#4f46e5',
  client: '#047857',
  document: '#64748b',
};

const FILE_ENTITY_TYPES = new Set(['file', 'project_file', 'knowledge_file', 'document']);
const PROJECT_QUOTATION_RELATIONS = new Set(['project_quotation', 'quotation_project']);
const QUOTATION_INVOICE_RELATIONS = new Set(['quotation_invoice', 'invoice_quotation']);
const PROJECT_INVOICE_RELATIONS = new Set(['project_invoice', 'invoice_project', 'invoice_address_project']);
const PROJECT_DETAIL_HIDDEN_RELATIONS = PROJECT_QUOTATION_RELATIONS;

const PROJECT_DETAIL_STAGES: Array<{ id: ProjectDetailStageId; label: string; x: number }> = [
  { id: 'files', label: 'Files later', x: 92 },
  { id: 'task', label: 'Tasks', x: 250 },
  { id: 'invoice', label: 'Invoices', x: 408 },
  { id: 'project', label: 'Project', x: 585 },
  { id: 'email', label: 'Email', x: 880 },
];

const PROJECT_DETAIL_FOCUS = { x: 680, y: 304 };

const PROJECT_DETAIL_CLUSTER_LAYOUT: Record<Exclude<ProjectDetailStageId, 'project'>, {
  x: number;
  yMin: number;
  yMax: number;
  maxRows: number;
  lane: GraphNodeWithPosition['lane'];
  direction: -1 | 1;
}> = {
  files: { x: 255, yMin: 386, yMax: 496, maxRows: 4, lane: 'support', direction: -1 },
  task: { x: 310, yMin: 122, yMax: 302, maxRows: 6, lane: 'incoming', direction: -1 },
  invoice: { x: 500, yMin: 360, yMax: 472, maxRows: 4, lane: 'outgoing', direction: -1 },
  email: { x: 830, yMin: 122, yMax: 270, maxRows: 4, lane: 'outgoing', direction: 1 },
  support: { x: 495, yMin: 470, yMax: 520, maxRows: 3, lane: 'support', direction: -1 },
};

const PROJECT_DETAIL_CLUSTER_LABELS: Array<{ id: ProjectDetailStageId; label: string; x: number; y: number }> = [
  { id: 'task', label: 'Tasks', x: 190, y: 84 },
  { id: 'email', label: 'Email / users', x: 766, y: 84 },
  { id: 'files', label: 'Files / documents', x: 190, y: 352 },
  { id: 'invoice', label: 'Invoices', x: 428, y: 352 },
  { id: 'project', label: 'Project', x: 632, y: 214 },
];

const PROJECT_DETAIL_ZONE_BACKDROPS: Array<{
  id: ProjectDetailStageId;
  x: number;
  y: number;
  width: number;
  height: number;
}> = [
  { id: 'task', x: 150, y: 96, width: 270, height: 230 },
  { id: 'email', x: 744, y: 96, width: 160, height: 210 },
  { id: 'files', x: 150, y: 364, width: 250, height: 150 },
  { id: 'invoice', x: 412, y: 340, width: 190, height: 168 },
  { id: 'project', x: 612, y: 226, width: 170, height: 180 },
];

function normalizeErrorMessage(error: unknown) {
  return error instanceof Error ? error.message : String(error || 'GraphRAG visualizer failed.');
}

function formatPercent(value: number) {
  return `${Math.round(Math.max(0, Math.min(1, value)) * 100)}%`;
}

function formatRelationType(value: string) {
  return value.replace(/_/g, ' ');
}

function nodeTitle(node: CompanyAiGraphVisualNode) {
  return node.displayName || node.businessKey || `${node.entityType} ${node.entityId}`;
}

function nodeUpdatedTime(node: CompanyAiGraphVisualNode) {
  const time = Date.parse(String(node.updatedUtc || ''));
  return Number.isFinite(time) ? time : 0;
}

function formatNodeUpdatedDate(node: CompanyAiGraphVisualNode) {
  const time = nodeUpdatedTime(node);
  if (!time) return 'No date';
  return new Intl.DateTimeFormat(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(new Date(time));
}

function sortRecentProjectNodes(nodes: CompanyAiGraphVisualNode[]) {
  return nodes
    .filter((node) => node.entityType === 'project')
    .sort((left, right) => (
      nodeUpdatedTime(right) - nodeUpdatedTime(left)
      || Number(right.entityNodeId || 0) - Number(left.entityNodeId || 0)
      || nodeTitle(left).localeCompare(nodeTitle(right))
    ));
}

function edgeTitle(edge: CompanyAiGraphVisualEdge, nodesById: Map<string, CompanyAiGraphVisualNode>) {
  const from = nodesById.get(edge.from);
  const to = nodesById.get(edge.to);
  return `${from ? nodeTitle(from) : edge.from} -> ${to ? nodeTitle(to) : edge.to}`;
}

function typePriority(entityType: string) {
  const priority: Record<string, number> = {
    project: 9000,
    quotation: 7800,
    invoice: 7600,
    task: 6800,
    address: 5400,
    client: 4800,
    user: 3200,
    email: 1200,
    document: 1000,
  };
  return priority[entityType] || 1500;
}

function chooseDefaultNode(nodes: CompanyAiGraphVisualNode[]): string | null {
  const [mostRecentProject] = sortRecentProjectNodes(nodes);
  if (mostRecentProject) return mostRecentProject.id;

  const [best] = [...nodes].sort((left, right) => {
    const leftScore = typePriority(left.entityType) + Number(left.degree || 0);
    const rightScore = typePriority(right.entityType) + Number(right.degree || 0);
    return rightScore - leftScore || nodeTitle(left).localeCompare(nodeTitle(right));
  });

  return best?.id || null;
}

function projectDetailStageForNode(node: CompanyAiGraphVisualNode): ProjectDetailStageId {
  if (node.entityType === 'project') return 'project';
  if (FILE_ENTITY_TYPES.has(node.entityType)) return 'files';
  if (node.entityType === 'task') return 'task';
  if (node.entityType === 'invoice') return 'invoice';
  if (node.entityType === 'email') return 'email';
  return 'support';
}

function countProjectDetailStages(nodes: CompanyAiGraphVisualNode[]) {
  const counts = new Map<ProjectDetailStageId, number>();
  PROJECT_DETAIL_STAGES.forEach((stage) => counts.set(stage.id, 0));
  nodes.forEach((node) => {
    const stage = projectDetailStageForNode(node);
    counts.set(stage, (counts.get(stage) || 0) + 1);
  });
  return counts;
}

function summarizeGraph(nodes: CompanyAiGraphVisualNode[], edges: CompanyAiGraphVisualEdge[]) {
  const nodeTypes = Array.from(nodes.reduce((map, node) => {
    map.set(node.entityType, (map.get(node.entityType) || 0) + 1);
    return map;
  }, new Map<string, number>()).entries())
    .map(([entityType, count]) => ({ entityType, count }))
    .sort((left, right) => right.count - left.count || left.entityType.localeCompare(right.entityType));

  const relationTypes = Array.from(edges.reduce((map, edge) => {
    map.set(edge.relationType, (map.get(edge.relationType) || 0) + 1);
    return map;
  }, new Map<string, number>()).entries())
    .map(([relationType, count]) => ({ relationType, count }))
    .sort((left, right) => right.count - left.count || left.relationType.localeCompare(right.relationType));

  return {
    nodeCount: nodes.length,
    edgeCount: edges.length,
    nodeTypes,
    relationTypes,
  };
}

function otherEdgeNodeId(edge: CompanyAiGraphVisualEdge, nodeId: string) {
  if (edge.from === nodeId) return edge.to;
  if (edge.to === nodeId) return edge.from;
  return null;
}

function buildProjectInvoiceBridgeEdges(
  graph: CompanyAiGraphVisualization,
  selectedNodeId: string,
  candidateEdges: CompanyAiGraphVisualEdge[]
): CompanyAiGraphVisualEdge[] {
  const nodesById = new Map(graph.nodes.map((node) => [node.id, node]));
  const selectedNode = nodesById.get(selectedNodeId);
  if (selectedNode?.entityType !== 'project') return [];

  const directInvoiceNodeIds = new Set<string>();
  candidateEdges.forEach((edge) => {
    if (!PROJECT_INVOICE_RELATIONS.has(edge.relationType)) return;
    const invoiceNodeId = otherEdgeNodeId(edge, selectedNodeId);
    if (invoiceNodeId && nodesById.get(invoiceNodeId)?.entityType === 'invoice') {
      directInvoiceNodeIds.add(invoiceNodeId);
    }
  });

  const projectQuotationEdges = new Map<string, CompanyAiGraphVisualEdge>();
  candidateEdges.forEach((edge) => {
    if (!PROJECT_QUOTATION_RELATIONS.has(edge.relationType)) return;
    const quotationNodeId = otherEdgeNodeId(edge, selectedNodeId);
    if (quotationNodeId && nodesById.get(quotationNodeId)?.entityType === 'quotation') {
      projectQuotationEdges.set(quotationNodeId, edge);
    }
  });

  const bridgeEdges: CompanyAiGraphVisualEdge[] = [];
  projectQuotationEdges.forEach((projectQuotationEdge, quotationNodeId) => {
    const quotationNode = nodesById.get(quotationNodeId);
    candidateEdges.forEach((edge) => {
      if (!QUOTATION_INVOICE_RELATIONS.has(edge.relationType)) return;
      const invoiceNodeId = otherEdgeNodeId(edge, quotationNodeId);
      const invoiceNode = invoiceNodeId ? nodesById.get(invoiceNodeId) : null;
      if (!invoiceNodeId || invoiceNode?.entityType !== 'invoice' || directInvoiceNodeIds.has(invoiceNodeId)) return;

      bridgeEdges.push({
        id: `visual-project-invoice-${selectedNodeId}-${quotationNodeId}-${invoiceNodeId}`,
        from: selectedNodeId,
        to: invoiceNodeId,
        relationType: 'project_invoice',
        confidence: Math.min(projectQuotationEdge.confidence, edge.confidence),
        source: 'quotation_bridge',
        evidence: {
          relationPath: 'project->quotation->invoice',
          projectQuotationEdgeId: projectQuotationEdge.id,
          quotationInvoiceEdgeId: edge.id,
          quotationNodeId: quotationNode?.entityNodeId || quotationNodeId,
          quotation: quotationNode ? nodeTitle(quotationNode) : null,
          invoiceNodeId: invoiceNode.entityNodeId,
          invoice: nodeTitle(invoiceNode),
        },
        needsRepair: Boolean(projectQuotationEdge.needsRepair || edge.needsRepair),
      });
      directInvoiceNodeIds.add(invoiceNodeId);
    });
  });

  return bridgeEdges;
}

function buildDisplayGraph(
  graph: CompanyAiGraphVisualization,
  selectedNodeId: string | null,
  viewMode: GraphViewMode,
  relationType: string,
  minConfidence: number
): CompanyAiGraphVisualization {
  const confidenceEdges = graph.edges.filter((edge) => edge.confidence >= minConfidence);
  const filteredEdges = confidenceEdges.filter((edge) => {
    if (relationType !== 'all' && edge.relationType !== relationType) return false;
    return true;
  });

  if (viewMode === 'detail' && selectedNodeId) {
    const bridgeEdges = relationType === 'all' || relationType === 'project_invoice'
      ? buildProjectInvoiceBridgeEdges(graph, selectedNodeId, confidenceEdges)
      : [];
    const detailEdges = filteredEdges.filter((edge) => !PROJECT_DETAIL_HIDDEN_RELATIONS.has(edge.relationType));
    const projectDetailEdges = [...detailEdges, ...bridgeEdges];
    const directEdges = projectDetailEdges.filter((edge) => edge.from === selectedNodeId || edge.to === selectedNodeId);
    const nodeIds = new Set<string>([selectedNodeId]);
    directEdges.forEach((edge) => {
      nodeIds.add(edge.from);
      nodeIds.add(edge.to);
    });
    const nodes = graph.nodes.filter((node) => nodeIds.has(node.id));
    const focusEdges = projectDetailEdges.filter((edge) => nodeIds.has(edge.from) && nodeIds.has(edge.to));
    return {
      nodes,
      edges: focusEdges,
      summary: summarizeGraph(nodes, focusEdges),
    };
  }

  const nodeIds = new Set<string>();
  filteredEdges.forEach((edge) => {
    nodeIds.add(edge.from);
    nodeIds.add(edge.to);
  });

  if (selectedNodeId) {
    nodeIds.add(selectedNodeId);
  }

  const nodes = graph.nodes.filter((node) => nodeIds.has(node.id));
  const visibleEdges = filteredEdges.filter((edge) => nodeIds.has(edge.from) && nodeIds.has(edge.to));
  return {
    nodes,
    edges: visibleEdges,
    summary: summarizeGraph(nodes, visibleEdges),
  };
}

function getConnectedNodeIds(edges: CompanyAiGraphVisualEdge[], selectedNodeId: string | null) {
  const connected = new Set<string>();
  if (!selectedNodeId) return connected;
  edges.forEach((edge) => {
    if (edge.from === selectedNodeId) connected.add(edge.to);
    if (edge.to === selectedNodeId) connected.add(edge.from);
  });
  return connected;
}

function buildOverviewLayout(graph: CompanyAiGraphVisualization): GraphNodeWithPosition[] {
  const width = 1000;
  const height = 560;
  const types = Array.from(new Set(graph.nodes.map((node) => node.entityType))).sort();
  const indexByType = new Map<string, number>();
  const countByType = graph.nodes.reduce((map, node) => {
    map.set(node.entityType, (map.get(node.entityType) || 0) + 1);
    return map;
  }, new Map<string, number>());

  return graph.nodes.map((node, index) => {
    const typeIndex = indexByType.get(node.entityType) || 0;
    indexByType.set(node.entityType, typeIndex + 1);
    const typeCount = countByType.get(node.entityType) || 1;
    const typePosition = Math.max(types.indexOf(node.entityType), 0);
    const columnCount = Math.max(types.length, 1);
    const x = columnCount === 1
      ? width / 2
      : 100 + (typePosition * ((width - 200) / Math.max(columnCount - 1, 1)));
    const rowCount = Math.min(18, typeCount);
    const columnOffset = Math.floor(typeIndex / Math.max(rowCount, 1));
    const rowIndex = typeIndex % Math.max(rowCount, 1);
    const y = typeCount === 1
      ? height / 2
      : 64 + (rowIndex * ((height - 128) / Math.max(Math.min(typeCount, rowCount) - 1, 1)));
    const xOffset = columnOffset * 38 * (index % 2 === 0 ? 1 : -1);
    return {
      ...node,
      x: Math.max(44, Math.min(width - 44, x + xOffset)),
      y,
      color: ENTITY_COLORS[node.entityType] || '#334155',
      lane: 'overview',
    };
  });
}

function buildProjectDetailLayout(graph: CompanyAiGraphVisualization, selectedNodeId: string): GraphNodeWithPosition[] {
  const width = 1000;
  const height = 560;
  const selected = graph.nodes.find((node) => node.id === selectedNodeId);
  const positioned = new Map<string, GraphNodeWithPosition>();

  if (selected) {
    positioned.set(selected.id, {
      ...selected,
      x: PROJECT_DETAIL_FOCUS.x,
      y: PROJECT_DETAIL_FOCUS.y,
      color: ENTITY_COLORS[selected.entityType] || '#334155',
      lane: 'focus',
    });
  }

  const nodesByStage = graph.nodes.reduce((map, node) => {
    if (node.id === selectedNodeId) return map;
    const stage = projectDetailStageForNode(node);
    const next = map.get(stage) || [];
    next.push(node);
    map.set(stage, next);
    return map;
  }, new Map<ProjectDetailStageId, CompanyAiGraphVisualNode[]>());

  const placeStage = (stage: ProjectDetailStageId, nodes: CompanyAiGraphVisualNode[]) => {
    if (stage === 'project') return;
    const cluster = PROJECT_DETAIL_CLUSTER_LAYOUT[stage as Exclude<ProjectDetailStageId, 'project'>] || PROJECT_DETAIL_CLUSTER_LAYOUT.support;
    const sorted = [...nodes].sort((left, right) => (
      right.degree - left.degree
      || typePriority(right.entityType) - typePriority(left.entityType)
      || nodeTitle(left).localeCompare(nodeTitle(right))
    ));
    const maxRows = cluster.maxRows;
    sorted.forEach((node, index) => {
      const column = Math.floor(index / maxRows);
      const row = index % maxRows;
      const rowsInColumn = Math.min(maxRows, sorted.length - (column * maxRows));
      const y = rowsInColumn === 1
        ? (cluster.yMin + cluster.yMax) / 2
        : cluster.yMin + (row * ((cluster.yMax - cluster.yMin) / Math.max(rowsInColumn - 1, 1)));
      const rowNudge = row % 2 === 0 ? 0 : 12 * cluster.direction;
      positioned.set(node.id, {
        ...node,
        x: Math.max(44, Math.min(width - 44, cluster.x + (column * 46 * cluster.direction) + rowNudge)),
        y,
        color: ENTITY_COLORS[node.entityType] || '#334155',
        lane: cluster.lane,
      });
    });
  };

  PROJECT_DETAIL_STAGES.forEach((stage) => {
    placeStage(stage.id, nodesByStage.get(stage.id) || []);
  });
  placeStage('support', nodesByStage.get('support') || []);

  return graph.nodes.map((node) => positioned.get(node.id) || {
    ...node,
    x: width / 2,
    y: height / 2,
    color: ENTITY_COLORS[node.entityType] || '#334155',
    lane: 'other',
  });
}

function buildLayout(graph: CompanyAiGraphVisualization, selectedNodeId: string | null, viewMode: GraphViewMode): GraphNodeWithPosition[] {
  if (viewMode === 'detail' && selectedNodeId && graph.nodes.some((node) => node.id === selectedNodeId)) {
    return buildProjectDetailLayout(graph, selectedNodeId);
  }

  return buildOverviewLayout(graph);
}

function shouldShowNodeLabel(args: {
  node: GraphNodeWithPosition;
  selectedNodeId: string | null;
  connectedNodeIds: Set<string>;
  labelMode: GraphLabelMode;
  nodeCount: number;
}) {
  if (args.labelMode === 'none') return false;
  if (args.labelMode === 'all') return true;
  if (args.node.id === args.selectedNodeId) return true;
  if (args.node.degree >= 8) return true;
  return args.nodeCount <= 18 && args.connectedNodeIds.has(args.node.id) && args.node.entityType !== 'email';
}

function relationVisual(edge: CompanyAiGraphVisualEdge) {
  if (PROJECT_INVOICE_RELATIONS.has(edge.relationType) || QUOTATION_INVOICE_RELATIONS.has(edge.relationType)) {
    return { marker: 'invoice', color: '#3a9b5f', dash: '' };
  }
  if (edge.relationType.includes('task') || edge.relationType.includes('action')) {
    return { marker: 'task', color: '#7c4dd8', dash: edge.relationType.includes('action') ? '8 7' : '' };
  }
  if (edge.relationType.includes('email')) {
    return { marker: 'email', color: '#b83268', dash: '' };
  }
  if (edge.relationType.includes('reference')) {
    return { marker: 'reference', color: '#748094', dash: '5 7' };
  }
  return { marker: 'default', color: '#7f8da3', dash: '' };
}

function graphEdgePath(from: GraphNodeWithPosition, to: GraphNodeWithPosition, viewMode: GraphViewMode) {
  if (viewMode !== 'detail') {
    return `M ${from.x} ${from.y} L ${to.x} ${to.y}`;
  }

  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const horizontal = Math.max(58, Math.min(190, Math.abs(dx) * 0.46));
  const verticalBend = Math.max(-78, Math.min(78, dy * 0.22));
  const fromDirection = dx >= 0 ? 1 : -1;
  const toDirection = dx >= 0 ? -1 : 1;

  return [
    `M ${from.x} ${from.y}`,
    `C ${from.x + horizontal * fromDirection} ${from.y + verticalBend}`,
    `${to.x + horizontal * toDirection} ${to.y - verticalBend}`,
    `${to.x} ${to.y}`,
  ].join(' ');
}

function graphEdgeLabelPosition(from: GraphNodeWithPosition, to: GraphNodeWithPosition, viewMode: GraphViewMode) {
  if (viewMode !== 'detail') {
    return {
      x: (from.x + to.x) / 2,
      y: (from.y + to.y) / 2 - 6,
    };
  }

  const x = from.x + (to.x - from.x) * 0.52;
  const y = from.y + (to.y - from.y) * 0.52 - 10;
  return { x, y };
}

function shouldShowEdgeLabel(edge: CompanyAiGraphVisualEdge, related: boolean, edgeCount: number, viewMode: GraphViewMode, selected: boolean) {
  if (viewMode !== 'detail') return false;
  if (!related) return false;
  if (selected) return true;
  if (edge.relationType === 'project_task') return false;
  if (edgeCount <= 18) return true;
  return edge.confidence >= 0.9 || edge.source === 'live_pmapp';
}

function nodeLabelPlacement(node: GraphNodeWithPosition, selected: boolean, viewMode: GraphViewMode) {
  if (viewMode !== 'detail' || selected) {
    return {
      x: 0,
      y: 17,
      anchor: 'middle' as const,
      side: false,
    };
  }

  if (node.x < PROJECT_DETAIL_FOCUS.x - 40) {
    return {
      x: -16,
      y: 4,
      anchor: 'end' as const,
      side: true,
    };
  }

  return {
    x: 16,
    y: 4,
    anchor: 'start' as const,
    side: true,
  };
}

function graphNodeRadius(node: GraphNodeWithPosition, selected: boolean) {
  if (selected) return node.entityType === 'project' ? 28 : 22;
  if (node.entityType === 'project') return 18;
  if (node.entityType === 'invoice') return 11.5;
  if (node.entityType === 'task') return 12.5;
  if (FILE_ENTITY_TYPES.has(node.entityType)) return 10.5;
  return Math.min(17, 7 + Math.sqrt(Math.max(1, node.degree)) * 1.8);
}

function GraphSvg({
  graph,
  selectedNodeId,
  selectedEdgeId,
  viewMode,
  labelMode,
  onSelectNode,
  onSelectEdge,
}: {
  graph: CompanyAiGraphVisualization;
  selectedNodeId: string | null;
  selectedEdgeId: string | null;
  viewMode: GraphViewMode;
  labelMode: GraphLabelMode;
  onSelectNode: (nodeId: string) => void;
  onSelectEdge: (edgeId: string) => void;
}) {
  const nodes = useMemo(() => buildLayout(graph, selectedNodeId, viewMode), [graph, selectedNodeId, viewMode]);
  const nodesById = useMemo(() => new Map(nodes.map((node) => [node.id, node])), [nodes]);
  const connectedNodeIds = useMemo(() => getConnectedNodeIds(graph.edges, selectedNodeId), [graph.edges, selectedNodeId]);

  return (
    <svg className="company-ai-graph-svg" viewBox="0 0 1000 560" role="img" aria-label="Entity graph visualization">
      <defs>
        {[
          { id: 'default', color: '#7f8da3' },
          { id: 'invoice', color: '#3a9b5f' },
          { id: 'task', color: '#7c4dd8' },
          { id: 'email', color: '#b83268' },
          { id: 'reference', color: '#748094' },
        ].map((marker) => (
          <marker
            key={marker.id}
            id={`company-ai-graph-arrow-${marker.id}`}
            markerWidth="10"
            markerHeight="10"
            refX="8"
            refY="3"
            orient="auto"
          >
            <path d="M0,0 L0,6 L9,3 z" fill={marker.color} />
          </marker>
        ))}
      </defs>
      {viewMode === 'detail' ? (
        <g aria-hidden="true">
          {PROJECT_DETAIL_ZONE_BACKDROPS.map((zone) => (
            <rect
              key={zone.id}
              x={zone.x}
              y={zone.y}
              width={zone.width}
              height={zone.height}
              rx="18"
              className={`company-ai-graph-zone company-ai-graph-zone--${zone.id}`}
            />
          ))}
          {PROJECT_DETAIL_CLUSTER_LABELS.map((stage) => (
            <g key={stage.id} className="company-ai-graph-stage-marker">
              <circle cx={stage.x} cy={stage.y - 4} r="3" className={`company-ai-graph-stage-dot company-ai-graph-stage-dot--${stage.id}`} />
              <text x={stage.x + 10} y={stage.y} textAnchor="start" className="company-ai-graph-stage-label">
                {stage.label}
              </text>
            </g>
          ))}
        </g>
      ) : null}
      {graph.edges.map((edge) => {
        const from = nodesById.get(edge.from);
        const to = nodesById.get(edge.to);
        if (!from || !to) return null;
        const selected = edge.id === selectedEdgeId;
        const related = Boolean(selectedNodeId && (edge.from === selectedNodeId || edge.to === selectedNodeId));
        const visual = relationVisual(edge);
        const path = graphEdgePath(from, to, viewMode);
        const labelPosition = graphEdgeLabelPosition(from, to, viewMode);
        const showEdgeLabel = shouldShowEdgeLabel(edge, related, graph.edges.length, viewMode, selected);
        return (
          <g key={edge.id} className="company-ai-graph-edge-group">
            <path
              d={path}
              className="company-ai-graph-edge-hitbox"
              onClick={() => onSelectEdge(edge.id)}
            />
            <path
              d={path}
              className={`company-ai-graph-edge${selected ? ' company-ai-graph-edge--selected' : ''}${related ? ' company-ai-graph-edge--related' : ''}${edge.needsRepair ? ' company-ai-graph-edge--repair' : ''}`}
              stroke={selected ? '#111827' : visual.color}
              strokeDasharray={edge.needsRepair ? '7 5' : visual.dash}
              strokeWidth={selected ? 3.2 : 0.85 + Math.max(0.25, edge.confidence) * 1.35}
              markerEnd={`url(#company-ai-graph-arrow-${visual.marker})`}
              onClick={() => onSelectEdge(edge.id)}
            />
            {showEdgeLabel ? (
              <text
                x={labelPosition.x}
                y={labelPosition.y}
                className="company-ai-graph-edge-label"
                textAnchor="middle"
                fill={selected ? '#111827' : visual.color}
              >
                {formatRelationType(edge.relationType).slice(0, 24)}
              </text>
            ) : null}
            <title>{formatRelationType(edge.relationType)} - {formatPercent(edge.confidence)}</title>
          </g>
        );
      })}
      {nodes.map((node) => {
        const selected = node.id === selectedNodeId;
        const related = connectedNodeIds.has(node.id);
        const radius = graphNodeRadius(node, selected);
        const showLabel = shouldShowNodeLabel({
          node,
          selectedNodeId,
          connectedNodeIds,
          labelMode,
          nodeCount: graph.nodes.length,
        });
        const labelPlacement = nodeLabelPlacement(node, selected, viewMode);
        return (
          <g
            key={node.id}
            className={`company-ai-graph-node${selected ? ' company-ai-graph-node--selected' : ''}${related ? ' company-ai-graph-node--related' : ''}${node.lane === 'other' ? ' company-ai-graph-node--muted' : ''}`}
            transform={`translate(${node.x} ${node.y})`}
            onClick={() => onSelectNode(node.id)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onSelectNode(node.id);
              }
            }}
            role="button"
            tabIndex={0}
            aria-label={`${node.entityType}: ${nodeTitle(node)}`}
          >
            <circle
              r={radius + (selected ? 8 : 5)}
              className="company-ai-graph-node-ring"
              fill={selected ? node.color : 'transparent'}
            />
            <circle
              r={radius}
              fill={node.color}
              className={node.needsRepair ? 'company-ai-graph-node-dot company-ai-graph-node-dot--repair' : 'company-ai-graph-node-dot'}
            />
            {showLabel ? (
              <text
                x={labelPlacement.x}
                y={labelPlacement.side ? labelPlacement.y : radius + labelPlacement.y}
                textAnchor={labelPlacement.anchor}
                className={`company-ai-graph-node-label${labelPlacement.side ? ' company-ai-graph-node-label--side' : ''}`}
              >
                {nodeTitle(node).slice(0, selected ? 38 : 24)}
              </text>
            ) : null}
            <title>{node.entityType}: {nodeTitle(node)}</title>
          </g>
        );
      })}
    </svg>
  );
}

export default function GraphRagVisualizer({ canAccess }: GraphRagVisualizerProps) {
  const [entityType, setEntityType] = useState('project');
  const [entityId, setEntityId] = useState('');
  const [query, setQuery] = useState('');
  const [limit, setLimit] = useState('36');
  const [viewMode, setViewMode] = useState<GraphViewMode>('detail');
  const [relationType, setRelationType] = useState('all');
  const [minConfidence, setMinConfidence] = useState('0');
  const [labelMode, setLabelMode] = useState<GraphLabelMode>('key');
  const [graph, setGraph] = useState<CompanyAiGraphVisualization | null>(null);
  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [selectedEdgeId, setSelectedEdgeId] = useState<string | null>(null);

  async function loadGraph() {
    if (!canAccess) return;
    setLoading(true);
    setFeedback(null);
    try {
      const nextGraph = await companyAiService.getEntityGraphVisualization({
        entityType,
        entityId,
        query,
        limit: Number(limit),
      });
      setGraph(nextGraph);
      setSelectedNodeId(chooseDefaultNode(nextGraph.nodes));
      setSelectedEdgeId(null);
      setRelationType('all');
    } catch (error) {
      setGraph(null);
      setFeedback(normalizeErrorMessage(error));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadGraph();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canAccess]);

  const nodesById = useMemo(() => new Map((graph?.nodes || []).map((node) => [node.id, node])), [graph]);
  const selectedNode = selectedNodeId ? nodesById.get(selectedNodeId) || null : null;
  const graphView = useMemo(() => {
    if (!graph) return null;
    return buildDisplayGraph(graph, selectedNodeId, viewMode, relationType, Number(minConfidence) || 0);
  }, [graph, selectedNodeId, viewMode, relationType, minConfidence]);
  const visibleNodesById = useMemo(() => new Map((graphView?.nodes || []).map((node) => [node.id, node])), [graphView]);
  const selectedEdge = selectedEdgeId ? (graphView?.edges || []).find((edge) => edge.id === selectedEdgeId) || null : null;
  const recentProjectNodes = useMemo(() => sortRecentProjectNodes(graph?.nodes || []).slice(0, 12), [graph]);
  const projectDetailStageCounts = useMemo(() => countProjectDetailStages(graphView?.nodes || []), [graphView]);
  const relationTypeOptions = useMemo(() => {
    const relationTypes = graph?.summary.relationTypes || [];
    return [
      { value: 'all', label: 'All relations' },
      ...relationTypes.map((item) => ({
        value: item.relationType,
        label: `${formatRelationType(item.relationType)} (${item.count})`,
      })),
    ];
  }, [graph]);
  const visibleSelectedConnections = useMemo(() => {
    if (!graphView || !selectedNodeId) return [];
    return graphView.edges
      .filter((edge) => edge.from === selectedNodeId || edge.to === selectedNodeId)
      .sort((left, right) => (
        Number(right.confidence || 0) - Number(left.confidence || 0)
        || formatRelationType(left.relationType).localeCompare(formatRelationType(right.relationType))
      ));
  }, [graphView, selectedNodeId]);
  const hiddenNodeCount = Math.max(0, (graph?.summary.nodeCount || 0) - (graphView?.summary.nodeCount || 0));
  const hiddenEdgeCount = Math.max(0, (graph?.summary.edgeCount || 0) - (graphView?.summary.edgeCount || 0));

  useEffect(() => {
    if (!graph || !selectedNodeId) return;
    if (!graph.nodes.some((node) => node.id === selectedNodeId)) {
      setSelectedNodeId(chooseDefaultNode(graph.nodes));
    }
  }, [graph, selectedNodeId]);

  useEffect(() => {
    if (!selectedEdgeId || !graphView) return;
    if (!graphView.edges.some((edge) => edge.id === selectedEdgeId)) {
      setSelectedEdgeId(null);
    }
  }, [graphView, selectedEdgeId]);

  if (!canAccess) {
    return (
      <PageSection width="wide" padding="compact" className="company-ai-admin-section">
        <EmptyState
          icon={<Network size={28} />}
          title="GraphRAG visualizer is admin-only"
          description="The entity graph includes internal relationship evidence, email hints, and repair status."
        />
      </PageSection>
    );
  }

  return (
    <PageSection width="full" padding="compact" className="company-ai-admin-section company-ai-graph-section">
      <div className="company-ai-layout company-ai-layout--graph">
        <div className="company-ai-primary">
          <Card
            header={(
              <CardHeader
                title="GraphRAG Visualizer"
                subtitle="Inspect PMapp entity nodes, relationship edges, confidence, source, and raw graph evidence."
                actions={graph ? <StatusBadge status="ready">{graph.summary.nodeCount} nodes</StatusBadge> : undefined}
              />
            )}
          >
            <div className="company-ai-graph-controls">
              <SearchInput
                aria-label="Search graph"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                onClear={() => setQuery('')}
                placeholder="Search address, user, project code, invoice, quotation, or email hint"
                fullWidth
                density="compact"
              />
              <Select
                aria-label="Graph entity type"
                value={entityType}
                onChange={(event) => setEntityType(event.target.value)}
                options={ENTITY_TYPE_OPTIONS}
                density="compact"
                fullWidth
              />
              <input
                aria-label="Graph entity id"
                value={entityId}
                onChange={(event) => setEntityId(event.target.value)}
                placeholder="Entity id or business key"
                className="company-ai-graph-input"
              />
              <Select
                aria-label="Graph node limit"
                value={limit}
                onChange={(event) => setLimit(event.target.value)}
                options={LIMIT_OPTIONS}
                density="compact"
              />
              <Button
                type="button"
                onClick={loadGraph}
                disabled={loading}
                iconBefore={<RefreshCcw size={16} aria-hidden="true" />}
              >
                {loading ? 'Loading' : 'Refresh'}
              </Button>
            </div>

            {graph ? (
              <div className="company-ai-graph-display-controls">
                <Select
                  aria-label="Graph view mode"
                  value={viewMode}
                  onChange={(event) => setViewMode(event.target.value as GraphViewMode)}
                  options={VIEW_MODE_OPTIONS}
                  density="compact"
                  fullWidth
                />
                <Select
                  aria-label="Graph relation filter"
                  value={relationType}
                  onChange={(event) => setRelationType(event.target.value)}
                  options={relationTypeOptions}
                  density="compact"
                  fullWidth
                />
                <Select
                  aria-label="Graph confidence filter"
                  value={minConfidence}
                  onChange={(event) => setMinConfidence(event.target.value)}
                  options={MIN_CONFIDENCE_OPTIONS}
                  density="compact"
                  fullWidth
                />
                <Select
                  aria-label="Graph label mode"
                  value={labelMode}
                  onChange={(event) => setLabelMode(event.target.value as GraphLabelMode)}
                  options={LABEL_MODE_OPTIONS}
                  density="compact"
                  fullWidth
                />
              </div>
            ) : null}

            {feedback ? (
              <div className="company-ai-feedback company-ai-feedback--error">{feedback}</div>
            ) : null}

            {graph && graph.nodes.length ? (
              <div className="company-ai-graph-workspace">
                {recentProjectNodes.length ? (
                  <aside className="company-ai-graph-project-pane" aria-label="User to recent projects">
                    <div className="company-ai-graph-project-pane__header">
                      <span>User -&gt; recent projects</span>
                      <span>{recentProjectNodes.length} shown</span>
                    </div>
                    <div className="company-ai-graph-project-pane__list">
                      {recentProjectNodes.map((project) => {
                        const selected = project.id === selectedNodeId;
                        return (
                          <button
                            type="button"
                            key={project.id}
                            className={`company-ai-graph-project-button${selected ? ' company-ai-graph-project-button--selected' : ''}`}
                            aria-pressed={selected}
                            onClick={() => {
                              setSelectedNodeId(project.id);
                              setSelectedEdgeId(null);
                              setViewMode('detail');
                            }}
                          >
                            <strong>{project.businessKey || project.entityId}</strong>
                            <span>{nodeTitle(project)}</span>
                            <em>{formatNodeUpdatedDate(project)}</em>
                          </button>
                        );
                      })}
                    </div>
                  </aside>
                ) : null}

                <div className="company-ai-graph-detail-pane">
                  {graphView && viewMode === 'detail' ? (
                    <div className="company-ai-graph-stage-strip" aria-label="Selected project detail stages">
                      {PROJECT_DETAIL_STAGES.map((stage) => {
                        const count = projectDetailStageCounts.get(stage.id) || 0;
                        return (
                          <span
                            key={stage.id}
                            className={`company-ai-graph-stage-pill${stage.id === 'files' ? ' company-ai-graph-stage-pill--pending' : ''}`}
                          >
                            <span>{stage.label}</span>
                            <strong>{count}</strong>
                          </span>
                        );
                      })}
                    </div>
                  ) : null}

                  {graphView && graphView.nodes.length ? (
                    <div className="company-ai-graph-canvas">
                      <div className="company-ai-graph-canvas-meta">
                        <span>{graphView.summary.nodeCount} visible nodes</span>
                        <span>{graphView.summary.edgeCount} visible edges</span>
                        {hiddenNodeCount || hiddenEdgeCount ? (
                          <span>{hiddenNodeCount} nodes, {hiddenEdgeCount} edges hidden</span>
                        ) : null}
                      </div>
                      <GraphSvg
                        graph={graphView}
                        selectedNodeId={selectedNodeId}
                        selectedEdgeId={selectedEdgeId}
                        viewMode={viewMode}
                        labelMode={labelMode}
                        onSelectNode={(nodeId) => {
                          setSelectedNodeId(nodeId);
                          setSelectedEdgeId(null);
                        }}
                        onSelectEdge={(edgeId) => {
                          setSelectedEdgeId(edgeId);
                        }}
                      />
                    </div>
                  ) : (
                    <EmptyState
                      icon={<Filter size={28} />}
                      title="No visible relationships"
                      description="Change the relation or confidence filters to bring matching edges back into view."
                    />
                  )}
                </div>
              </div>
            ) : !loading ? (
              <EmptyState
                icon={<Network size={28} />}
                title="No graph rows found"
                description="Try a broader search, a different entity type, or run the full entity graph backfill after deployment."
              />
            ) : (
              <div className="company-ai-muted">Loading graph relationships...</div>
            )}
          </Card>
        </div>

        <div className="company-ai-sidebar">
          <Card
            header={(
              <CardHeader
                title="Subgraph Summary"
                subtitle="Counts reflect the bounded visualizer result, not the entire graph."
              />
            )}
          >
            {graph ? (
              <div className="company-ai-stacked">
                <div className="company-ai-inline-metrics">
                  <div className="company-ai-inline-metric">
                    <strong>{graph.summary.nodeCount}</strong>
                    <span>loaded nodes</span>
                  </div>
                  <div className="company-ai-inline-metric">
                    <strong>{graph.summary.edgeCount}</strong>
                    <span>loaded edges</span>
                  </div>
                  <div className="company-ai-inline-metric">
                    <strong>{graphView?.summary.nodeCount || 0}</strong>
                    <span>visible nodes</span>
                  </div>
                  <div className="company-ai-inline-metric">
                    <strong>{graphView?.summary.edgeCount || 0}</strong>
                    <span>visible edges</span>
                  </div>
                </div>
                <div className="company-ai-source-strip">
                  {(graphView?.summary.nodeTypes || graph.summary.nodeTypes).map((item) => (
                    <span key={item.entityType} className="company-ai-source-pill">{item.entityType}: {item.count}</span>
                  ))}
                </div>
                <div className="company-ai-source-strip">
                  {(graphView?.summary.relationTypes || graph.summary.relationTypes).slice(0, 10).map((item) => (
                    <span key={item.relationType} className="company-ai-source-pill">{formatRelationType(item.relationType)}: {item.count}</span>
                  ))}
                </div>
              </div>
            ) : (
              <div className="company-ai-muted">Run the visualizer to see graph counts.</div>
            )}
          </Card>

          <Card
            header={(
              <CardHeader
                title={selectedEdge ? 'Selected Edge' : 'Selected Node'}
                subtitle={selectedEdge ? 'Relationship source and evidence' : 'Graph node details'}
              />
            )}
          >
            {selectedEdge && graph ? (
              <div className="company-ai-stacked">
                <div className="company-ai-result-field">
                  <div className="company-ai-result-label">Path</div>
                  <div className="company-ai-result-value">{edgeTitle(selectedEdge, nodesById)}</div>
                </div>
                <div className="company-ai-source-strip">
                  <span className="company-ai-source-pill">{formatRelationType(selectedEdge.relationType)}</span>
                  <span className="company-ai-source-pill">{formatPercent(selectedEdge.confidence)}</span>
                  <span className="company-ai-source-pill">{selectedEdge.source || 'unknown source'}</span>
                  {selectedEdge.needsRepair ? <span className="company-ai-source-pill">Needs repair</span> : null}
                </div>
                <details className="company-ai-graph-evidence-details" open>
                  <summary>Evidence JSON</summary>
                  <pre className="company-ai-graph-json">{JSON.stringify(selectedEdge.evidence || {}, null, 2)}</pre>
                </details>
              </div>
            ) : selectedNode ? (
              <div className="company-ai-stacked">
                <div className="company-ai-result-field">
                  <div className="company-ai-result-label">Display</div>
                  <div className="company-ai-result-value">{nodeTitle(selectedNode)}</div>
                </div>
                <div className="company-ai-source-strip">
                  <span className="company-ai-source-pill">{selectedNode.entityType}</span>
                  <span className="company-ai-source-pill">ID {selectedNode.entityId}</span>
                  {selectedNode.businessKey ? <span className="company-ai-source-pill">{selectedNode.businessKey}</span> : null}
                  <span className="company-ai-source-pill">Degree {selectedNode.degree}</span>
                  <span className="company-ai-source-pill">{visibleSelectedConnections.length} visible links</span>
                  {selectedNode.needsRepair ? <span className="company-ai-source-pill">Needs repair</span> : null}
                </div>
                <div className="company-ai-action-list">
                  {visibleSelectedConnections
                    .slice(0, 18)
                    .map((edge) => (
                      <button
                        type="button"
                        key={edge.id}
                        className="company-ai-action company-ai-graph-edge-button"
                        onClick={() => setSelectedEdgeId(edge.id)}
                      >
                        <div className="company-ai-action-title">{formatRelationType(edge.relationType)} - {formatPercent(edge.confidence)}</div>
                        <div className="company-ai-action-copy">
                          {edge.from === selectedNode.id ? 'To' : 'From'} {' '}
                          {nodeTitle(visibleNodesById.get(edge.from === selectedNode.id ? edge.to : edge.from) || nodesById.get(edge.from === selectedNode.id ? edge.to : edge.from) || selectedNode)}
                          {edge.source ? ` - ${edge.source}` : ''}
                        </div>
                      </button>
                    ))}
                  {!visibleSelectedConnections.length ? (
                    <div className="company-ai-muted">No visible relationships match the current display filters.</div>
                  ) : null}
                </div>
              </div>
            ) : (
              <div className="company-ai-muted">Select a node or edge in the graph.</div>
            )}
          </Card>
        </div>
      </div>
    </PageSection>
  );
}
